/**
 * Why-This-Matters — small on-demand AI explainer widget.
 *
 * Usage:
 *   <WhyThisMatters
 *      kind="overdue"                  // "anomaly" | "overdue" | "large_txn" | "cashflow" | "vendor"
 *      title="Ambuja Cements ₹48L overdue 22 days"
 *      context={{ party, amount, days_overdue, invoice_no }}
 *      compact={false}                 // compact = inline button; expanded = panel
 *   />
 *
 * Fetches from POST /api/ai/explain on click. Result renders "WHY IT MATTERS" +
 * bulleted "RECOMMENDED ACTIONS". Provider is disclosed at the bottom.
 */
import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export default function WhyThisMatters({ kind = "custom", title, context = {}, compact = false, label = "Why this matters?" }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [open, setOpen] = useState(false);

    const run = async () => {
        if (busy) return;
        if (result) { setOpen(!open); return; }
        setBusy(true);
        try {
            const r = await api.post("/ai/explain", { kind, title, context });
            setResult(r.data);
            setOpen(true);
        } catch (e) {
            toast.error("AI explain failed: " + (e.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    };

    return (
        <div className={compact ? "inline-block" : "block"}>
            <button
                data-testid={`why-btn-${kind}`}
                onClick={run}
                disabled={busy}
                className="inline-flex items-center gap-1 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] border border-[var(--brand-amber)]/40 px-2 py-1 hover:bg-[var(--brand-amber)] hover:text-black transition-colors disabled:opacity-40"
            >
                {busy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                {busy ? "Thinking…" : label}
            </button>
            {open && result && (
                <div data-testid="why-result" className="mt-2 border border-[var(--brand-amber)]/40 bg-[var(--brand-amber-dim)]/40 p-3 space-y-2">
                    {result.explanation && (
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] mb-1">Why It Matters</div>
                            <div className="text-xs font-body text-[var(--text-primary)]/90 whitespace-pre-wrap leading-relaxed">{result.explanation}</div>
                        </div>
                    )}
                    {result.actions && result.actions.length > 0 && (
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--pos)] mb-1">Recommended Actions</div>
                            <ol className="list-decimal list-inside space-y-1 text-xs font-body text-[var(--text-primary)]/85">
                                {result.actions.map((a, i) => <li key={i} className="pl-1">{a}</li>)}
                            </ol>
                        </div>
                    )}
                    <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-2 pt-2 border-t border-panel/40">
                        via {result.provider} · <button onClick={() => setOpen(false)} className="hover:text-[var(--text-primary)]">collapse</button>
                    </div>
                </div>
            )}
        </div>
    );
}
